import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';
import { formatJson } from '@/_shared/lib/format';
import { oneLight, SyntaxHighlighter } from '@/_shared/lib/syntax';

import type { ActivityItem } from './tool-call-meta';
import { ToolIcon } from './ToolIcons';

/** Extract a short distinguishing label from tool args when summary is empty/generic. */
function extractArgLabel(args: unknown): string {
  if (!args || typeof args !== 'object') return '';
  const a = args as Record<string, unknown>;
  // Try common identifier fields
  for (const key of [
    'source_name',
    'name',
    'source_id',
    'destination_id',
    'id',
    'path',
    'file_path',
    'table_name',
    'query',
    'pattern',
  ]) {
    const v = a[key];
    if (typeof v === 'string' && v.length > 0) {
      const display = v.length > 60 ? v.slice(0, 60) + '\u2026' : v;
      return display;
    }
  }
  return '';
}

// ---------------------------------------------------------------------------
// Sub-grouping: collapse consecutive items with the same action
// ---------------------------------------------------------------------------

interface SubGroup {
  action: string;
  items: ActivityItem[];
}

function buildSubGroups(items: ActivityItem[]): SubGroup[] {
  const groups: SubGroup[] = [];
  let current: SubGroup | null = null;

  for (const item of items) {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- optional chain narrows to `never` here (TS 5.9 bug)
    if (current && current.action === item.toolUse.action) {
      current.items.push(item);
    } else {
      if (current) groups.push(current);
      current = { action: item.toolUse.action, items: [item] };
    }
  }
  if (current) groups.push(current);
  return groups;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ActivityGroupProps {
  items: ActivityItem[];
  isActive?: boolean;
  /** Last assistant message before this tool group — shown as a compact context label. */
  context?: string;
  /** Current thinking text — shown with shimmer at the bottom of the active group. */
  thinkingText?: string;
}

// ---------------------------------------------------------------------------
// ActivityItemRow — single tool-use row (expandable)
// ---------------------------------------------------------------------------

function ActivityItemRow({
  item,
  isLast,
  isActive,
}: {
  item: ActivityItem;
  isLast: boolean;
  isActive: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const showPulse = isLast && isActive && !item.toolResult;

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <li>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ToolIcon category={item.toolUse.category} action={item.toolUse.action} />
            <span className="flex-1 truncate">
              {item.toolUse.friendlyName}
              {item.toolUse.summary && <span className="ml-1.5 text-muted-foreground/60">{item.toolUse.summary}</span>}
            </span>
            {showPulse && (
              <span
                className="animate-thinking-dot inline-block h-2 w-2 rounded-full bg-brand"
                data-testid="active-indicator"
              />
            )}
            {open ? (
              <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight size={12} className="shrink-0 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border/50 px-3 py-2 text-xs">
            {item.toolUse.detail ? (
              <div className="overflow-hidden rounded-lg border border-border">
                <SyntaxHighlighter
                  language={item.toolUse.detail.language}
                  style={oneLight}
                  customStyle={{ margin: 0, padding: '0.75rem', fontSize: '0.8125rem' }}
                >
                  {item.toolUse.detail.content}
                </SyntaxHighlighter>
              </div>
            ) : (
              <>
                {item.toolUse.args != null && (
                  <pre className="mb-2 overflow-x-auto rounded bg-background p-2 text-muted-foreground">
                    {formatJson(item.toolUse.args)}
                  </pre>
                )}
              </>
            )}
            {item.toolResult && (
              <>
                <div className="my-1 border-t border-border/30" />
                <pre className="max-h-48 overflow-auto rounded bg-background p-2 text-muted-foreground">
                  {item.toolResult.content}
                </pre>
              </>
            )}
          </div>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// SubGroupRow — collapsed row for 2+ consecutive items with same action
// ---------------------------------------------------------------------------

function SubGroupRow({
  subGroup,
  isLast,
  isActive,
}: {
  subGroup: SubGroup;
  isLast: boolean;
  isActive: boolean;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const count = subGroup.items.length;
  const firstItem = subGroup.items[0]!;

  // Build label from groupedTitle template or fallback
  const groupedTitle = firstItem.toolUse.groupedTitle;
  const label = groupedTitle
    ? groupedTitle.replace('{count}', String(count))
    : `${firstItem.toolUse.friendlyName} x ${count}`;

  // Show pulse if this is the last sub-group and any item lacks a result
  const showPulse = isLast && isActive && subGroup.items.some((item) => !item.toolResult);

  const effectiveOpen = open;

  return (
    <Collapsible open={effectiveOpen} onOpenChange={setOpen} asChild>
      <li>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:text-foreground"
            data-testid="sub-group-trigger"
          >
            <ToolIcon category={firstItem.toolUse.category} action={firstItem.toolUse.action} />
            <span className="flex-1 truncate">{label}</span>
            {showPulse && (
              <span
                className="animate-thinking-dot inline-block h-2 w-2 rounded-full bg-brand"
                data-testid="active-indicator"
              />
            )}
            {effectiveOpen ? (
              <ChevronDown size={12} className="shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight size={12} className="shrink-0 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="list-none border-t border-border/30">
            {subGroup.items.map((item) => (
              <li key={item.toolUse.id} className="px-3 py-1">
                <div className="text-xs text-muted-foreground/60">
                  {item.toolUse.summary !== '' && item.toolUse.summary != null
                    ? item.toolUse.summary
                    : extractArgLabel(item.toolUse.args) || item.toolUse.friendlyName}
                </div>
                {item.toolUse.detail && (
                  <div className="mt-1 overflow-hidden rounded border border-border/50">
                    <SyntaxHighlighter
                      language={item.toolUse.detail.language}
                      style={oneLight}
                      customStyle={{ margin: 0, padding: '0.5rem', fontSize: '0.75rem' }}
                    >
                      {item.toolUse.detail.content}
                    </SyntaxHighlighter>
                  </div>
                )}
                {item.toolResult && (
                  <pre className="mt-1 max-h-24 overflow-auto rounded bg-background p-2 text-[0.6875rem] text-muted-foreground/50">
                    {item.toolResult.content}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </li>
    </Collapsible>
  );
}

// ---------------------------------------------------------------------------
// ActivityGroup — top-level grouping component
// ---------------------------------------------------------------------------

export function ActivityGroup({
  items,
  isActive = false,
  context,
  thinkingText,
}: ActivityGroupProps): React.ReactElement {
  const thinkingEl = thinkingText ? (
    <p className="animate-shimmer px-3 py-1.5 text-sm text-muted-foreground" data-testid="thinking-indicator">
      {thinkingText}
    </p>
  ) : null;

  // Single item: render without wrapping card to reduce noise
  if (items.length === 1) {
    return (
      <div className="mx-4 my-1.5" data-testid="activity-group">
        {context && (
          <p className="mb-1 text-sm text-muted-foreground" data-testid="activity-context">
            {context}
          </p>
        )}
        <ul className="list-none">
          <ActivityItemRow item={items[0]!} isLast isActive={isActive} />
        </ul>
        {thinkingEl}
      </div>
    );
  }

  const subGroups = buildSubGroups(items);

  return (
    <div className="mx-4 my-1.5" data-testid="activity-group">
      {context && (
        <p className="mb-1 text-sm text-muted-foreground" data-testid="activity-context">
          {context}
        </p>
      )}
      <div className={`rounded-lg border border-border bg-muted ${isActive ? 'activity-active' : ''}`}>
        <ul className="list-none divide-y divide-border/30">
          {subGroups.map((sg, i) => {
            const isLastSg = i === subGroups.length - 1;
            if (sg.items.length === 1) {
              return (
                <ActivityItemRow
                  key={sg.items[0]!.toolUse.id}
                  item={sg.items[0]!}
                  isLast={isLastSg}
                  isActive={isActive}
                />
              );
            }
            return <SubGroupRow key={sg.items[0]!.toolUse.id} subGroup={sg} isLast={isLastSg} isActive={isActive} />;
          })}
        </ul>
        {thinkingEl}
      </div>
    </div>
  );
}
